import { MAX_ADDITIONAL_GUESTS, validGuestCount } from "../shared/attendance";
import { useEffect, useMemo, useState } from "react";
import type { Locale, ResolveResponse, RsvpStatus } from "../shared/types";
import { api, ApiRequestError } from "./api";
import { Brand, LanguageSwitch, Spinner, text } from "./ui";
import { RsvpAnswerSummary } from "./RsvpAnswerSummary";

function formatDate(value: string, timezone: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-GB", {
    dateStyle: "full",
    timeStyle: "short",
    timeZone: timezone,
  }).format(new Date(value));
}

function calendarDate(value: string): string {
  return new Date(value).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function icsEscape(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
}

export function GuestRsvp({ eventSlug, locale, setLocale }: { eventSlug: string; locale: Locale; setLocale: (locale: Locale) => void }) {
  const [data, setData] = useState<ResolveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [calendarPromptOpen, setCalendarPromptOpen] = useState(false);
  const [status, setStatus] = useState<RsvpStatus>("pending");
  const [additionalGuests, setAdditionalGuests] = useState<number | "">(0);
  const [names, setNames] = useState<string[]>([]);
  const [dietary, setDietary] = useState("");
  const [note, setNote] = useState("");
  const encodedEmail = useMemo(() => new URLSearchParams(window.location.hash.slice(1)).get("u") ?? "", []);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!encodedEmail) {
        setError(text(locale, "Geçerli bir davet bağlantısı kullanın.", "Please use a valid invitation link."));
        setLoading(false);
        return;
      }
      try {
        const response = await api<ResolveResponse>("/api/public/invitations/resolve", {
          method: "POST",
          body: JSON.stringify({ eventSlug, u: encodedEmail }),
        });
        if (!active) return;
        setError("");
        setEditing(false);
        setData(response);
        setNames(response.invitation.guestNames || []);
        setStatus(response.invitation.status);
        setDietary(response.invitation.dietaryRequirements);
        setNote(response.invitation.rsvpNote);
        setAdditionalGuests(response.invitation.additionalGuests);
      } catch (reason) {
        if (active) setError(reason instanceof Error ? reason.message : "Invitation unavailable");
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
  }, [encodedEmail, eventSlug, locale]);

  async function submit() {
    if (!data || status === "pending") return;
    if (status === "confirmed" && !validGuestCount(additionalGuests)) {
      setError(text(locale, "Ek misafir sayısı 0–3 arasında bir tam sayı olmalıdır.", "Enter a whole number of additional guests from 0 to 3."));
      return;
    }
    setSaving(true);
    if (status === "confirmed" && Array.from({ length: Number(additionalGuests) }, (_, index) => names[index] || "").some((name) => !name.trim())) {
      setSaving(false);
      setError(text(locale, "Lütfen her ek misafirin adını ve soyadını girin.", "Please enter the full name of each additional guest."));
      return;
    }
    setError("");
    setSaved(false);
    try {
      const response = await api<ResolveResponse>("/api/public/rsvp", {
        method: "PUT",
        body: JSON.stringify({
          eventSlug,
          u: encodedEmail,
          revision: data.invitation.revision,
          status,
          dietaryRequirements: dietary,
          rsvpNote: note,
          additionalGuests: status === "confirmed" ? additionalGuests : 0,
          guestNames: status === "confirmed" ? Array.from({ length: Number(additionalGuests) }, (_, index) => names[index] || "") : [],
        }),
      });
      setData(response);
      setStatus(response.invitation.status);
      setAdditionalGuests(response.invitation.additionalGuests);
      setSaved(true);
      setEditing(false);
      setCalendarPromptOpen(response.invitation.status === "confirmed");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (reason) {
      if (reason instanceof ApiRequestError && reason.status === 409 && reason.payload.event && reason.payload.invitation) {
        const fresh = reason.payload as unknown as ResolveResponse;
        setData(fresh);
        setNames(fresh.invitation.guestNames || []);
        setStatus(fresh.invitation.status);
        setDietary(fresh.invitation.dietaryRequirements);
        setNote(fresh.invitation.rsvpNote);
        setAdditionalGuests(fresh.invitation.additionalGuests);
      }
      setError(reason instanceof Error ? reason.message : text(locale, "Yanıt kaydedilemedi.", "Unable to save your response."));
    } finally {
      setSaving(false);
    }
  }

  function cancelEditing() {
    if (!data) return;
    setStatus(data.invitation.status);
    setNames(data.invitation.guestNames || []);
    setAdditionalGuests(data.invitation.additionalGuests);
    setDietary(data.invitation.dietaryRequirements);
    setNote(data.invitation.rsvpNote);
    setError("");
    setSaved(false);
    setEditing(false);
  }

  function downloadCalendar() {
    if (!data) return;
    const event = data.event;
    const content = [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Echos Education//Events//EN", "BEGIN:VEVENT",
      `UID:${event.slug}@events.echosedu.com`, `DTSTAMP:${calendarDate(new Date().toISOString())}`,
      `DTSTART:${calendarDate(event.startsAt)}`, `DTEND:${calendarDate(event.endsAt)}`,
      `SUMMARY:${icsEscape(event.title[locale])}`, `DESCRIPTION:${icsEscape(event.description[locale])}`,
      `LOCATION:${icsEscape(`${event.venue[locale]}, ${event.address}`)}`, "END:VEVENT", "END:VCALENDAR",
    ].join("\r\n");
    const url = URL.createObjectURL(new Blob([content], { type: "text/calendar;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${event.slug}.ics`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const googleCalendarUrl = data ? `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(data.event.title[locale])}&dates=${calendarDate(data.event.startsAt)}/${calendarDate(data.event.endsAt)}&details=${encodeURIComponent(data.event.description[locale])}&location=${encodeURIComponent(`${data.event.venue[locale]}, ${data.event.address}`)}` : "#";
  const showSummary = Boolean(data && data.invitation.status !== "pending" && !editing);

  return (
    <div className="guest-page">
      <header className="guest-header">
        <Brand />
        <LanguageSwitch locale={locale} onChange={setLocale} />
      </header>
      <main className="guest-main">
        {loading && <section className="guest-card guest-card--loading"><Spinner label={text(locale, "Davetiniz hazırlanıyor…", "Preparing your invitation…")} /></section>}
        {!loading && error && !data && (
          <section className="guest-card guest-error-card">
            <span className="eyebrow">ECHOS EVENTS</span>
            <h1>{text(locale, "Davet bağlantısı kullanılamıyor", "Invitation unavailable")}</h1>
            <p>{error}</p>
            <a className="text-link" href="mailto:info@echosedu.com">info@echosedu.com <span>↗</span></a>
          </section>
        )}
        {data && (
          <>
            <section className="event-hero">
              <div className="event-hero__copy">

                <h1>{data.event.title[locale]}</h1>
                <p>{data.event.description[locale]}</p>
              </div>
              <div className="event-details">
                <div><span>{text(locale, "TARİH VE SAAT", "DATE & TIME")}</span><strong>{formatDate(data.event.startsAt, data.event.timezone, locale)}</strong></div>
                <div><span>{text(locale, "MEKÂN", "VENUE")}</span><strong>{data.event.venue[locale]}</strong><small>{data.event.address}</small></div>
                {data.event.mapUrl && <a href={data.event.mapUrl} target="_blank" rel="noreferrer">{text(locale, "Haritada aç", "Open map")} ↗</a>}
              </div>
            </section>
            <section className="guest-card rsvp-card">
              <div className="rsvp-card__heading">
                <div>
                  <span className="eyebrow">RSVP</span>
                  <h2>{text(locale, `Merhaba ${data.invitation.firstName},`, `Hello ${data.invitation.firstName},`)}</h2>
                  {!showSummary && <p>{text(locale, "Bu özel akşamda bize katılabilecek misiniz?", "Will you be able to join us for this special evening?")}</p>}
                </div>
              </div>
              {showSummary && <RsvpAnswerSummary invitation={data.invitation} locale={locale} saved={saved} onChange={() => { setEditing(true); setSaved(false); setError(""); }} />}
              {!showSummary && <fieldset className="rsvp-editor" disabled={saving} aria-label={text(locale, "Davet yanıtı", "Invitation response")}>
              <div className="response-options" role="radiogroup" aria-label="RSVP">
                <button type="button" role="radio" aria-checked={status === "confirmed"} className={status === "confirmed" ? "selected confirmed" : ""} onClick={() => { setStatus("confirmed"); setSaved(false); }} disabled={!data.invitation.editable}>
                  <span className="option-icon">✓</span><span><strong>{text(locale, "Katılıyorum", "I’ll be there")}</strong><small>{text(locale, "Sizinle buluşmayı sabırsızlıkla bekliyoruz.", "We look forward to welcoming you.")}</small></span>
                </button>
                <button type="button" role="radio" aria-checked={status === "declined"} className={status === "declined" ? "selected declined" : ""} onClick={() => { setStatus("declined"); setSaved(false); }} disabled={!data.invitation.editable}>
                  <span className="option-icon">×</span><span><strong>{text(locale, "Katılamıyorum", "I can’t attend")}</strong><small>{text(locale, "Sizi başka bir etkinlikte görmeyi umuyoruz.", "We hope to see you another time.")}</small></span>
                </button>
              </div>
              {status === "confirmed" && (
                <div className="rsvp-fields">
                  <div className="guest-count-field">
                    <div className="guest-count-copy"><label htmlFor="additional-guests">{text(locale, "Ek misafir", "Additional guests")}</label><p id="guest-count-help">{text(locale, "Yanınızda gelecek kişi sayısı, kendiniz hariç.", "People coming with you, not including yourself.")}</p></div>
                    <div className="guest-count-controls"><div className="guest-count-stepper">
                      <button type="button" disabled={!data.invitation.editable || Number(additionalGuests) <= 0} aria-label={text(locale, "Misafir sayısını azalt", "Decrease guest count")} onClick={() => { setAdditionalGuests(Math.max(0, Number(additionalGuests) - 1)); setSaved(false); }}>−</button>
                      <input id="additional-guests" type="number" min={0} max={MAX_ADDITIONAL_GUESTS} step={1} inputMode="numeric" value={additionalGuests} onChange={(event) => { setAdditionalGuests(event.target.value === "" ? "" : Number(event.target.value)); setSaved(false); }} onBlur={() => { if (additionalGuests === "") setAdditionalGuests(0); }} disabled={!data.invitation.editable} aria-describedby="guest-count-help guest-count-total" />
                      <button type="button" disabled={!data.invitation.editable || Number(additionalGuests) >= MAX_ADDITIONAL_GUESTS} aria-label={text(locale, "Misafir sayısını artır", "Increase guest count")} onClick={() => { setAdditionalGuests(Math.min(MAX_ADDITIONAL_GUESTS, Number(additionalGuests) + 1)); setSaved(false); }}>+</button>
                    </div><span id="guest-count-total" aria-live="polite">{validGuestCount(additionalGuests) ? additionalGuests === 0 ? text(locale, "Yalnız geliyorum", "Just me") : text(locale, `Sizinle birlikte ${1 + additionalGuests} kişi`, `${1 + additionalGuests} people including you`) : ""}</span></div>
                  </div>
                  {Array.from({ length: Math.min(MAX_ADDITIONAL_GUESTS, Number(additionalGuests)) }, (_, index) => <label key={index}>{text(locale, `Misafir ${index + 1} — Adı ve soyadı`, `Guest ${index + 1} — Full name`)}<input required maxLength={120} value={names[index] || ""} disabled={!data.invitation.editable} onChange={(e) => { const value = e.target.value; setNames((current) => { const next = [...current]; next[index] = value; return next; }); }} /></label>)}
                  <label>{text(locale, "Beslenme gereksinimleri", "Dietary requirements")} <span>{text(locale, "isteğe bağlı", "optional")}</span>
                    <textarea value={dietary} maxLength={500} onChange={(event) => { setDietary(event.target.value); setSaved(false); }} placeholder={text(locale, "Alerji veya özel beslenme gereksinimlerinizi yazın", "Tell us about allergies or dietary requirements")} disabled={!data.invitation.editable} />
                  </label>
                  <label>{text(locale, "Eklemek istediğiniz not", "A note for the team")} <span>{text(locale, "isteğe bağlı", "optional")}</span>
                    <textarea value={note} maxLength={1000} onChange={(event) => { setNote(event.target.value); setSaved(false); }} placeholder={text(locale, "Echos ekibine iletmek istediğiniz bir mesaj", "Anything you would like the Echos team to know")} disabled={!data.invitation.editable} />
                  </label>
                </div>
              )}
              {data.invitation.editable && <button className="primary-button" type="button" onClick={submit} disabled={saving || status === "pending"}>{saving ? text(locale, "Kaydediliyor…", "Saving…") : text(locale, "Yanıtımı kaydet", "Save my response")} <span>→</span></button>}
              {editing && <button className="outline-button rsvp-editor__cancel" type="button" onClick={cancelEditing}>{text(locale, "Vazgeç", "Cancel")}</button>}
              </fieldset>}
              {error && <div className="inline-error" role="alert">{error}</div>}
              {!data.invitation.editable && <div className="deadline-note">{text(locale, "RSVP son tarihi geçti. Değişiklik için lütfen bizimle iletişime geçin.", "The RSVP deadline has passed. Please contact us to make a change.")}</div>}
              {showSummary && data.invitation.status === "confirmed" && (
                <div className="calendar-actions">
                  <span>{text(locale, "TAKVİMİNİZE EKLEYİN", "ADD TO YOUR CALENDAR")}</span>
                  <div><a href={googleCalendarUrl} target="_blank" rel="noreferrer">Google Calendar ↗</a><button type="button" onClick={downloadCalendar}>Apple / Outlook ↓</button></div>
                </div>
              )}
            </section>
          </>
        )}
      </main>
      <footer className="guest-footer"><span>© Echos Education Ltd.</span><div><a href="https://echosedu.com/privacy-notice">{text(locale, "Gizlilik", "Privacy")}</a><a href="mailto:info@echosedu.com">{text(locale, "İletişim", "Contact")}</a></div></footer>
      {calendarPromptOpen && data && (
        <div className="modal-backdrop calendar-prompt-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setCalendarPromptOpen(false); }}>
          <section className="calendar-prompt" role="dialog" aria-modal="true" aria-labelledby="calendar-prompt-title">
            <button className="calendar-prompt__close" type="button" onClick={() => setCalendarPromptOpen(false)} aria-label={text(locale, "Kapat", "Close")}>×</button>
            <span className="calendar-prompt__icon" aria-hidden="true">✓</span>
            <span className="eyebrow">{text(locale, "YANITINIZ KAYDEDİLDİ", "RESPONSE SAVED")}</span>
            <h2 id="calendar-prompt-title">{text(locale, "Etkinliği takviminize eklemek ister misiniz?", "Would you like to add the event to your calendar?")}</h2>
            <p>{formatDate(data.event.startsAt, data.event.timezone, locale)}<br />{data.event.venue[locale]}</p>
            <div className="calendar-prompt__actions">
              <a className="primary-button" href={googleCalendarUrl} target="_blank" rel="noreferrer" onClick={() => setCalendarPromptOpen(false)}>Google Calendar ↗</a>
              <button className="outline-button" type="button" onClick={() => { downloadCalendar(); setCalendarPromptOpen(false); }}>Apple / Outlook ↓</button>
            </div>
            <button className="text-button calendar-prompt__later" type="button" onClick={() => setCalendarPromptOpen(false)}>{text(locale, "Şimdi değil", "Not now")}</button>
          </section>
        </div>
      )}
    </div>
  );
}
